import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { guides } from "../../../../services/TicketsServices";
import ConfirmDeleteDialog from "../shared/ConfirmDeleteDialog";
import GuideFormDialog from "../shared/GuideFormDialog";
import SimpleConfigTable from "../shared/SimpleConfigTable";
import TablePagerBar from "../shared/TablePagerBar";
import useGuides from "../shared/useGuides";

export default function GuidesPanel() {
  const navigate = useNavigate();
  const {
    items,
    activeFilter,
    pagination,
    deleteTarget,
    deleteLoading,
    changePage,
    changePerPage,
    changeActiveFilter,
    requestDelete,
    cancelDelete,
    confirmDelete,
    fetchList,
  } = useGuides();

  const [open, setOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const columns = useMemo(
    () => [
      { key: "name", label: "Name" },
      { key: "phone", label: "Phone" },
      { key: "agent_name", label: "Agent name" },
      { key: "is_active_label", label: "Active" },
    ],
    []
  );

  const rows = useMemo(() => {
    return (items || []).map((x) => ({
      ...x,
      is_active_label: x?.is_active ? "Yes" : "No",
    }));
  }, [items]);

  const validationSchema = Yup.object({
    phone: Yup.string().required("Phone is required"),
    name: Yup.string().required("Name is required"),
    agent_name: Yup.string().required("Agent name is required"),
  });

  const handleAdd = () => {
    setEditingItem(null);
    setOpen(true);
  };

  const handleEdit = (row) => {
    setEditingItem(row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingItem(null);
  };

  const handleSubmit = async (values, helpers) => {
    helpers?.setSubmitting?.(true);
    try {
      const payload = {
        phone: values.phone,
        is_active: Boolean(values.is_active),
        translations: {
          en: {
            name: values.name,
            agent_name: values.agent_name,
          },
        },
      };

      if (editingItem?.id) {
        await guides("put", payload, null, editingItem.id);
      } else {
        await guides("post", payload);
      }

      handleClose();
      await fetchList(pagination.page, pagination.perPage, activeFilter);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to save guide:", err);
    } finally {
      helpers?.setSubmitting?.(false);
    }
  };

  const handleRowClick = (row) => {
    if (!row?.id) return;
    navigate(`/management/ticket-management/guides/${row.id}`);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Active</InputLabel>
            <Select label="Active" value={activeFilter} onChange={(e) => changeActiveFilter(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleAdd} className={"text-capitalize"}>
            Add Guide
          </Button>
        </Box>
      </Box>

      <SimpleConfigTable
        columns={columns}
        rows={rows}
        onEdit={handleEdit}
        onLogicalDelete={(id) => requestDelete({ id })}
        onRowClick={handleRowClick}
      />

      <TablePagerBar
        perPage={pagination.perPage}
        onPerPageChange={(e) => changePerPage(Number(e.target.value))}
        total={pagination.total}
        page={pagination.page}
        onPageChange={(_, p) => changePage(p)}
      />

      {open && (
        <GuideFormDialog
          open={open}
          editingItem={editingItem}
          onClose={handleClose}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        />
      )}

      <ConfirmDeleteDialog
        open={Boolean(deleteTarget?.id)}
        title={"Delete guide"}
        description={"Are you sure you want to delete this guide?"}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        loading={deleteLoading}
      />
    </Box>
  );
}
